import React, { useState } from 'react';
import { Scale, UserRound, Bell, BookOpen } from 'lucide-react';
import { isPushSupported, registerAndSubscribe, isNativeApp } from '../lib/pushNotifications';

const INTRO_KEY = 'mibeko_intro_seen';
const DISCLAIMER_KEY = 'mibeko_disclaimer_accepted';
const NAME_PROMPTED_KEY = 'mibeko_name_prompted';
const PUSH_PROMPTED_KEY = 'mibeko_push_prompted';
export const USER_NAME_KEY = 'mibeko_user_name';

const shouldPromptPush = () => {
  try {
    if (localStorage.getItem(PUSH_PROMPTED_KEY)) return false;
    if (!isPushSupported()) return false;
    if (isNativeApp()) return true;
    return Notification.permission === 'default';
  } catch {
    return false;
  }
};

const getInitialStep = () => {
  try {
    if (!localStorage.getItem(INTRO_KEY) && !localStorage.getItem(DISCLAIMER_KEY)) return 'intro';
    if (!localStorage.getItem(DISCLAIMER_KEY)) return 'disclaimer';
    if (!localStorage.getItem(NAME_PROMPTED_KEY)) return 'name';
    if (shouldPromptPush()) return 'push';
    return null;
  } catch {
    return 'intro';
  }
};

const DisclaimerModal = () => {
  const [step, setStep] = useState(getInitialStep);
  const [name, setName] = useState('');
  const [pushBusy, setPushBusy] = useState(false);

  if (!step) return null;

  const finishIntro = () => {
    try { localStorage.setItem(INTRO_KEY, '1'); } catch {}
    setStep('disclaimer');
  };

  if (step === 'intro') {
    return (
      <div
        data-testid="intro-splash"
        style={{
          position: 'fixed', inset: 0, zIndex: 10001,
          background: '#0B1220',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '32px 26px', textAlign: 'center',
          paddingTop: 'calc(32px + env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <img
          src="/icons/icon-192.png"
          alt="Mibeko"
          style={{ width: '84px', height: '84px', borderRadius: '20px', marginBottom: '22px', boxShadow: '0 12px 32px rgba(0,0,0,0.35)' }}
        />
        <h1
          data-testid="intro-title"
          style={{ fontSize: '24px', fontWeight: 800, color: '#fff', marginBottom: '16px' }}
        >
          C&apos;est quoi le droit ?
        </h1>
        <p style={{ fontSize: '15px', lineHeight: 1.7, color: 'rgba(255,255,255,0.92)', maxWidth: '420px', marginBottom: '14px' }}>
          Le droit, c&apos;est l&apos;ensemble des règles qui organisent la vie en société :
          ce que chacun peut faire, doit faire ou ne doit pas faire. Ces règles protègent
          votre famille, votre travail, vos biens et vos libertés.
        </p>
        <p style={{ fontSize: '15px', lineHeight: 1.7, color: 'rgba(255,255,255,0.92)', maxWidth: '420px', marginBottom: '30px' }}>
          En RDC, elles sont écrites dans la Constitution, les codes et les lois.
          <strong> Mibeko les met dans votre poche</strong> : cherchez, comprenez et
          connaissez vos droits, en langage simple.
        </p>
        <button
          onClick={finishIntro}
          data-testid="intro-start-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '14px 34px', background: '#fff', color: '#0B1220',
            border: 'none', borderRadius: '999px',
            fontSize: '15px', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <BookOpen style={{ width: 17, height: 17 }} /> Commencer
        </button>
      </div>
    );
  }

  const acceptDisclaimer = () => {
    try {
      localStorage.setItem(DISCLAIMER_KEY, '1');
      setStep(localStorage.getItem(NAME_PROMPTED_KEY) ? (shouldPromptPush() ? 'push' : null) : 'name');
    } catch {
      setStep('name');
    }
  };

  const finishName = (savedName) => {
    try {
      localStorage.setItem(NAME_PROMPTED_KEY, '1');
      if (savedName) localStorage.setItem(USER_NAME_KEY, savedName);
    } catch {
      // stockage indisponible
    }
    window.dispatchEvent(new Event('mibeko-name-changed'));
    setStep(shouldPromptPush() ? 'push' : null);
  };

  const submitName = (e) => {
    e.preventDefault();
    finishName(name.trim().slice(0, 30));
  };

  const finishPush = () => {
    try { localStorage.setItem(PUSH_PROMPTED_KEY, '1'); } catch {}
    setStep(null);
  };

  const enablePush = async () => {
    setPushBusy(true);
    try {
      await registerAndSubscribe();
    } catch {
      // permission refusee ou erreur : on ferme sans bloquer
    } finally {
      setPushBusy(false);
      finishPush();
    }
  };

  return (
    <div
      data-testid="disclaimer-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(11, 18, 32, 0.65)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        data-testid={step === 'disclaimer' ? 'disclaimer-modal' : step === 'name' ? 'name-modal' : 'push-modal'}
        role="dialog"
        aria-modal="true"
        style={{
          background: 'var(--bg-white)',
          borderRadius: '16px',
          padding: '28px 24px 24px',
          maxWidth: '420px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          textAlign: 'center',
        }}
      >
        <div
          className="icon-circle"
          style={{ width: '52px', height: '52px', margin: '0 auto 16px' }}
        >
          {step === 'disclaimer'
            ? <Scale style={{ width: '24px', height: '24px' }} />
            : step === 'name'
              ? <UserRound style={{ width: '24px', height: '24px' }} />
              : <Bell style={{ width: '24px', height: '24px' }} />}
        </div>

        {step === 'disclaimer' ? (
          <>
            <h2
              data-testid="disclaimer-title"
              style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '12px' }}
            >
              Avertissement juridique
            </h2>
            <p
              data-testid="disclaimer-message"
              style={{ fontSize: '14px', lineHeight: 1.6, color: 'var(--text-gray)', marginBottom: '20px' }}
            >
              Mibeko est un outil d&apos;information juridique. Les contenus diffusés ne
              remplacent en aucun cas la consultation d&apos;un avocat ou d&apos;un
              professionnel du droit.
            </p>
            <button
              onClick={acceptDisclaimer}
              data-testid="disclaimer-accept-btn"
              style={{
                width: '100%', padding: '13px',
                background: 'var(--primary)', color: '#fff',
                border: 'none', borderRadius: '10px',
                fontSize: '14px', fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              J&apos;ai compris
            </button>
          </>
        ) : step === 'name' ? (
          <>
            <h2
              data-testid="name-title"
              style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '12px' }}
            >
              Faisons connaissance !
            </h2>
            <p style={{ fontSize: '14px', lineHeight: 1.6, color: 'var(--text-gray)', marginBottom: '18px' }}>
              Comment devons-nous vous appeler ? Votre prénom restera uniquement sur votre appareil.
            </p>
            <form onSubmit={submitName}>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Votre prénom (ex : Éric)"
                maxLength={30}
                autoFocus
                data-testid="name-input"
                style={{
                  width: '100%', padding: '12px 14px',
                  border: '1px solid var(--border)', borderRadius: '10px',
                  fontSize: '14px', fontFamily: 'inherit',
                  background: 'var(--bg-white)', color: 'var(--text-dark)',
                  marginBottom: '14px', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <button
                type="submit"
                data-testid="name-submit-btn"
                style={{
                  width: '100%', padding: '13px',
                  background: 'var(--primary)', color: '#fff',
                  border: 'none', borderRadius: '10px',
                  fontSize: '14px', fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Continuer
              </button>
            </form>
            <button
              onClick={() => finishName('')}
              data-testid="name-skip-btn"
              style={{
                marginTop: '12px', background: 'none', border: 'none',
                color: 'var(--text-gray)', fontSize: '13px',
                cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline',
              }}
            >
              Passer cette étape
            </button>
          </>
        ) : (
          <>
            <h2
              data-testid="push-title"
              style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '12px' }}
            >
              Restez informé !
            </h2>
            <p style={{ fontSize: '14px', lineHeight: 1.6, color: 'var(--text-gray)', marginBottom: '20px' }}>
              Recevez chaque matin à 7h30 l&apos;article de loi du jour, et soyez averti
              dès qu&apos;une mise à jour de Mibeko est disponible.
            </p>
            <button
              onClick={enablePush}
              disabled={pushBusy}
              data-testid="push-enable-btn"
              style={{
                width: '100%', padding: '13px',
                background: 'var(--primary)', color: '#fff',
                border: 'none', borderRadius: '10px',
                fontSize: '14px', fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
                opacity: pushBusy ? 0.7 : 1,
              }}
            >
              {pushBusy ? 'Activation…' : 'Activer les notifications'}
            </button>
            <button
              onClick={finishPush}
              data-testid="push-skip-btn"
              style={{
                marginTop: '12px', background: 'none', border: 'none',
                color: 'var(--text-gray)', fontSize: '13px',
                cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline',
              }}
            >
              Plus tard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DisclaimerModal;
